// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getSession } from "../utils/sessionstore";
import { useDispatch } from "react-redux";
import {
  chatbotPop,
  floatingChatBotPop,
  hideChatbotPop,
  iframePop,
} from "../store/auth/action";
import { FaArrowRight, FaCaretRight } from "react-icons/fa";
import { logAmplitudeEvent } from "../utils/amplitude/functions";
import amplitude from "../utils/amplitude/initiate";
import { MdDragIndicator } from "react-icons/md";
import { RxCross2 } from "react-icons/rx";

const BUTTON_WIDTH = 175;
const BUTTON_HEIGHT = 41;
const EDGE_PADDING = 50;

const ChatAssistant = ({ setSalesContent }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });
  const hasDraggedRef = useRef(false);
  const { userDetails, subscriptions } = useSelector((state) => ({
    userDetails: state.userDetails,
    subscriptions: state.subscriptions,
  }));

  const { clientID, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const sessionId = getSession("session_id");
  const client_id = getSession("client_id");
  const requiredClientID = clientID || client_id;
  const requiredUserID = getSession("session_hwai");

  useEffect(() => {
    if (isOpen) {
      // Optional: logic when chat is opened
    }
  }, [isOpen]);

  useEffect(() => {
    const setInitialPosition = () => {
      setPosition({
        x: Math.max(
          EDGE_PADDING,
          window.innerWidth - BUTTON_WIDTH - EDGE_PADDING
        ),
        y: Math.max(EDGE_PADDING, window.innerHeight - BUTTON_HEIGHT - 108),
      });
    };

    const handleResize = () => {
      setPosition((prev) => ({
        x: Math.min(
          Math.max(EDGE_PADDING, prev.x),
          window.innerWidth - BUTTON_WIDTH - EDGE_PADDING
        ),
        y: Math.min(
          Math.max(EDGE_PADDING, prev.y),
          window.innerHeight - BUTTON_HEIGHT - EDGE_PADDING
        ),
      }));
    };

    setInitialPosition();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!isDragging) return;

      const deltaX = event.clientX - dragStartRef.current.x;
      const deltaY = event.clientY - dragStartRef.current.y;
      const nextX = Math.min(
        Math.max(EDGE_PADDING, dragStartRef.current.posX + deltaX),
        window.innerWidth - BUTTON_WIDTH - EDGE_PADDING
      );
      const nextY = Math.min(
        Math.max(EDGE_PADDING, dragStartRef.current.posY + deltaY),
        window.innerHeight - BUTTON_HEIGHT - EDGE_PADDING
      );

      if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
        hasDraggedRef.current = true;
      }

      setPosition({ x: nextX, y: nextY });
    };

    const handlePointerUp = () => setIsDragging(false);

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [isDragging]);

  const handlehide = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dispatch(floatingChatBotPop(false));
    window.localStorage.setItem("hideChatbot", "true");
  };

  const handlePointerDown = (event) => {
    dragStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      posX: position.x,
      posY: position.y,
    };
    hasDraggedRef.current = false;
    setIsDragging(true);
  };

  const handleClick = () => {
    if (hasDraggedRef.current) {
      hasDraggedRef.current = false;
      return;
    }

    const standardName = "GenieAI";
    if (!subscriptions?.length || !standardName) return;

    let localProductLine = "";
    let chatbotDashboard = null;

    for (const subscription of subscriptions) {
      const categories = subscription.productcategoryIds || [];

      for (const category of categories) {
        const dashboards = category.dashboardIds || [];

        // Find dashboard by standardName instead of ID
        const match = dashboards.find(
          (dash) => dash.standarddashboardId?.standardName === standardName
        );

        if (match) {
          chatbotDashboard = match;
          localProductLine = subscription.label;
          break;
        }
      }

      if (localProductLine) break; // exit outer loop
    }

    if (!chatbotDashboard || !chatbotDashboard._id) return;

    const url = chatbotDashboard?.url;

    if (url !== "Disabled") {
      // Step 4: Build the new path and navigate
      const dashboardId = chatbotDashboard._id;

      let basePath = "";

      // Check if we need to include clientID in the path
      if (clientID) {
        // We're in admin mode - always start with clientID
        basePath = `/${clientID}`;
      }
      // For normal users, basePath remains empty

      // Build the final path
      const dashboardPath = `/dashboard/${dashboardId}/SIDEBAR_DASHBOARD`;
      const finalPath = basePath
        ? `${basePath}${dashboardPath}`
        : dashboardPath;

      window.open(finalPath, "_blank");

      logAmplitudeEvent(amplitude, "Page Viewed", {
        triggerType: "Floating Button",
        standardDashboardName: "ChatBot",
        dashboardName: "Genie AI",
        clientName: JSON.parse(localStorage.getItem("clientname"))?.value,
        clientId: clientID ?? getSession("client_id"),
        targetType: "DASHBOARD",
        eventTimestamp: new Date().toLocaleString("en-US", { timeZone: "UTC" }),
      });
    } else {
      // If the URL is "Disabled", open the sales popup
      setSalesContent(chatbotDashboard?.salesContentId);
      setTimeout(() => dispatch(chatbotPop()), 0);
    }
  };

  return (
    <>
      <div
        className="fixed flex items-center justify-center rounded-full cursor-pointer bg-[#7D528B] border border-gray-200 select-none"
        style={{
          top: `${position.y}px`,
          left: `${position.x}px`,
          height: `${BUTTON_HEIGHT}px`,
          width: `${BUTTON_WIDTH}px`,
          padding: "8px 12px 8px 8px",
          gap: "8px",
          boxShadow:
            "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)",
          filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15))",
        }}
        onClick={handleClick}
      >
        <div
          className="flex items-center h-full text-white border-r pr-1 border-white/30 cursor-move"
          onPointerDown={handlePointerDown}
          onClick={(event) => event.stopPropagation()}
        >
          <MdDragIndicator size={18} />
        </div>
        <div
          className="fixed -top-[6px] -right-0 bg-[#f7dbff] rounded-full p-[2px] text-[#7D528B]"
          onClick={handlehide}
        >
          <RxCross2 size={12} />
        </div>
        <span
          style={{
            width: "25px",
            height: "21px",
            fontFamily: "Roboto",
            fontWeight: 700,
            fontSize: "14px",
            color: "#ffffff",
            whiteSpace: "nowrap",
            opacity: 0.8,
            fontStyle: "italic",
          }}
        >
          Ask
        </span>
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "4px" }}
        >
          <img
            src="https://hwlobbystorer1.blob.core.windows.net/icons/genieAI-white.svg?v=1"
            style={{
              width: "80px",
              objectFit: "contain",
              filter: "drop-shadow(0 5px 5px rgba(0,0,0,0.1))",
            }}
            alt="Chatbot Icon"
          />
        </div>
      </div>
    </>
  );
};

export default ChatAssistant;
